import {
  HHAlert,
  HHButton,
  HHChip,
  HHFormControlLabel,
  HHIconButton,
  HHLoadingButton,
  HHStack,
  HHSwitch,
  HHTable,
  HHTableBody,
  HHTableCell,
  HHTableContainer,
  HHTableHead,
  HHTableRow,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import Popover from '@mui/material/Popover';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { validate as isValidUUID } from 'uuid';
import { JsonTree } from '../../hinge-select/contracts/components/json-tree/json-tree';
import DetailsAccordion from '../components/details-accordion';
import {
  EligibilityCheckUseCase,
  useEligibilityVerifier,
  VerifierResult,
} from '../hooks/use-eligibility-verifier';
import {
  getMockedEfileVerifierResult,
  getMockedVerifierResult,
} from '../test-utils/fixtures/eligibility-verifier.fixture';
import {
  DiffRow,
  DiffStatus,
  isDivergent,
  resolveComparison,
} from '../utils/eligibility-compare';

export const ELIGIBILITY_VERIFIER_TEST_ID = 'eligibility-verifier-container';

// Both BFF endpoints validate inquiryDate against a YYYY-MM-DD regex, so a time
// component would be discarded on the wire — this is a date control, not a
// date-time one.
const INQUIRY_DATE_FORMAT = 'YYYY-MM-DD';

// Query params so a filled-in form can be shared as a URL. Kept to these two —
// the rest (from-cache, show-PHI) are per-viewer preferences, not part of what
// makes a shared link reproduce the same check.
const HH_USER_UUID_PARAM = 'hhUserUuid';
const INQUIRY_DATE_PARAM = 'inquiryDate';

// YYYY-MM-DD is already dayjs's native ISO parse, so a garbled param just comes
// back invalid rather than throwing.
const parseInquiryDateParam = (value: string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

// The mock loader is a UI-development affordance. This is a migration
// verification tool, so fake results that could be mistaken for a real check must
// never reach a deployed build — hence the guard here and the banner below.
const MOCK_ENABLED = process.env.NODE_ENV !== 'production';

// One button per provider, so each provider's comparison can be exercised on
// demand. Adding a provider's mock is one entry here.
const MOCK_LOADERS: {
  label: string;
  testId: string;
  load: () => VerifierResult;
}[] = [
  {
    label: 'Load ESI mock',
    testId: 'verifier-load-mock-esi',
    load: getMockedVerifierResult,
  },
  {
    label: 'Load E-file mock',
    testId: 'verifier-load-mock-efile',
    load: getMockedEfileVerifierResult,
  },
];

// Fixed for now — the operator-facing selector is removed until there's a reason
// to exercise the benefit gate too. Swap this back to a control rather than
// adding a second code path when that time comes.
const USE_CASE = EligibilityCheckUseCase.COVERAGE;

type ChipColor = 'success' | 'error' | 'warning' | 'info' | 'default';

const STATUS_CHIP: Record<DiffStatus, { label: string; color: ChipColor }> = {
  match: { label: 'Match', color: 'success' },
  mismatch: { label: 'Mismatch', color: 'error' },
  missingLegacy: { label: 'Missing on legacy', color: 'warning' },
  missingInternal: { label: 'Missing on internal', color: 'warning' },
  missingBoth: { label: 'Absent on both', color: 'default' },
  informational: { label: 'Info', color: 'info' },
};

const HEADER_CELL_SX = { backgroundColor: '#F5F5F5', fontWeight: 600 };
const VALUE_CELL_SX = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 13,
  wordBreak: 'break-all' as const,
};
// PHI values are blurred by default (see "Show me the real values" toggle).
// This is a screen-privacy affordance, not an access control — the values are
// still present in the DOM, just visually obscured. Rows carrying no member
// identifier opt out via `neverBlur`.
const BLUR_SX = { filter: 'blur(6px)' };
// The chip says how many fields diverged, not which. Redundant with its text
// label, so nothing is color-only.
const DIVERGENT_ROW_SX = { backgroundColor: '#fff1f0' };

const DiffTable = ({
  rows,
  blurred,
}: {
  rows: DiffRow[];
  blurred: boolean;
}): JSX.Element => {
  // Only sections whose fields differ per writer carry this column.
  const showAppliesTo = rows.some(row => row.appliesTo !== undefined);

  return (
    <HHTableContainer sx={{ border: '1px solid #E0E0E0', borderRadius: '8px' }}>
      <HHTable size="small">
        <HHTableHead>
          <HHTableRow>
            <HHTableCell sx={HEADER_CELL_SX}>Field</HHTableCell>
            {showAppliesTo && (
              <HHTableCell sx={HEADER_CELL_SX}>Applies to</HHTableCell>
            )}
            <HHTableCell sx={HEADER_CELL_SX}>Legacy</HHTableCell>
            <HHTableCell sx={HEADER_CELL_SX}>Internal</HHTableCell>
            <HHTableCell sx={HEADER_CELL_SX}>Status</HHTableCell>
          </HHTableRow>
        </HHTableHead>
        <HHTableBody>
          {rows.map(row => {
            const rowBlurred = blurred && !row.neverBlur;
            return (
              <HHTableRow
                key={row.key}
                data-testid={`diff-row-${row.key}`}
                sx={isDivergent(row.status) ? DIVERGENT_ROW_SX : undefined}
              >
                <HHTableCell>
                  <HHTypography hhVariant="body2">{row.label}</HHTypography>
                  {row.note && (
                    <HHTypography hhVariant="caption" color="text.secondary">
                      {row.note}
                    </HHTypography>
                  )}
                </HHTableCell>
                {showAppliesTo && (
                  <HHTableCell>
                    {row.appliesTo && (
                      <HHChip
                        hhVariant="outlined"
                        size="small"
                        label={row.appliesTo}
                      />
                    )}
                  </HHTableCell>
                )}
                <HHTableCell
                  sx={
                    rowBlurred
                      ? { ...VALUE_CELL_SX, ...BLUR_SX }
                      : VALUE_CELL_SX
                  }
                >
                  {row.legacy ?? '—'}
                </HHTableCell>
                <HHTableCell
                  sx={
                    rowBlurred
                      ? { ...VALUE_CELL_SX, ...BLUR_SX }
                      : VALUE_CELL_SX
                  }
                >
                  {row.internal ?? '—'}
                </HHTableCell>
                <HHTableCell>
                  <HHChip
                    hhVariant="filled"
                    size="small"
                    label={STATUS_CHIP[row.status].label}
                    color={STATUS_CHIP[row.status].color}
                  />
                </HHTableCell>
              </HHTableRow>
            );
          })}
        </HHTableBody>
      </HHTable>
    </HHTableContainer>
  );
};

const PayloadPanel = ({
  title,
  payload,
  error,
  blurred,
  titleVariant = 'h6',
  collapsible = false,
}: {
  title: string;
  payload?: unknown;
  error?: Error;
  blurred: boolean;
  titleVariant?: 'h6' | 'subtitle1';
  collapsible?: boolean;
}): JSX.Element => {
  const body = error ? (
    <HHAlert hhVariant="standard" severity="error">
      {error.message}
    </HHAlert>
  ) : (
    payload !== undefined && (
      <HHStack sx={blurred ? BLUR_SX : undefined}>
        <JsonTree value={payload} />
      </HHStack>
    )
  );

  return (
    <HHStack spacing={1} flex={1} minWidth="0">
      {/* Raw payloads collapse — a full response is hundreds of JsonTree nodes.
          An error never does; hiding why a side failed is the opposite of
          helpful. */}
      {collapsible && !error ? (
        <DetailsAccordion title={title} bordered>
          {body}
        </DetailsAccordion>
      ) : (
        <>
          <HHTypography hhVariant={titleVariant}>{title}</HHTypography>
          {body}
        </>
      )}
    </HHStack>
  );
};

const EligibilityVerifier = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [hhUserUuid, setHhUserUuid] = useState(
    () => searchParams.get(HH_USER_UUID_PARAM) ?? '',
  );
  const [inquiryDate, setInquiryDate] = useState<Dayjs | null>(() =>
    parseInquiryDateParam(searchParams.get(INQUIRY_DATE_PARAM)),
  );
  const [showRealValues, setShowRealValues] = useState(true);
  const blurred = !showRealValues;
  // On by default — avoids triggering a fresh (possibly billable) outbound
  // check on every submit; operators opt out per submit when they need a
  // live result.
  const [fromCache, setFromCache] = useState(true);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null,
  );

  // Mock state lives here rather than in the hook — the hook stays a pure
  // two-fetch wrapper with no test-only surface.
  const [mockResult, setMockResult] = useState<VerifierResult | undefined>();
  const { run, result: liveResult, loading } = useEligibilityVerifier();
  const result = mockResult ?? liveResult;

  const hhUserUuidValid = isValidUUID(hhUserUuid);
  // Keyboard entry into the picker yields an invalid Dayjs rather than null, and
  // formatting that produces the literal string "Invalid Date".
  const dateValid = inquiryDate !== null && inquiryDate.isValid();
  const submitDisabled = loading || !hhUserUuidValid || !dateValid;

  const comparison = resolveComparison(result?.legacy, result?.internal);
  const divergent =
    comparison.kind === 'compared'
      ? comparison.rows.filter(r => isDivergent(r.status)).length
      : 0;
  // Counted apart from the headline: the verifier can't tell which writer bills
  // this member, so an absent BCBSAL value on a BCBSMA member isn't a failure.
  const extraDivergent =
    comparison.kind === 'compared'
      ? comparison.extraColumns.filter(r => isDivergent(r.status)).length
      : 0;

  return (
    <HHStack
      spacing={4}
      padding={4}
      flex={1}
      minHeight="0"
      sx={{ overflow: 'auto' }}
      data-testid={ELIGIBILITY_VERIFIER_TEST_ID}
    >
      <HHStack spacing={3}>
        <HHTypography hhVariant="section-title">
          Eligibility Verifier
        </HHTypography>
        <HHTypography hhVariant="body2" color="text.secondary">
          Runs one member through the legacy eligibility service and HingeSelect
          internal eligibility, then compares both against the fields the
          downstream writer for the detected provider actually reads. E-file
          members are compared against the shared Waystar demographic block,
          plus the BCBSMA and BCBSAL invoice writers&apos; extra columns in a
          separate section. Other writers&apos; extra columns are not compared,
          so a clean result does not cover the Waystar subclasses that read
          them.
        </HHTypography>
      </HHStack>

      <HHStack direction="row" spacing={2} alignItems="flex-start">
        <HHTextField
          hhVariant="variant-bypass"
          variant="outlined"
          size="small"
          label="HH User UUID"
          placeholder="3fa85f64-5717-4562-b3fc-2c963f66afa6"
          value={hhUserUuid}
          onChange={(e): void => setHhUserUuid(e.target.value)}
          error={hhUserUuid !== '' && !hhUserUuidValid}
          sx={{ minWidth: 340 }}
          data-testid="verifier-hh-user-uuid"
        />
        <DatePicker
          label="Inquiry Date"
          value={inquiryDate}
          onChange={(newValue): void => setInquiryDate(newValue)}
          disabled={loading}
          renderInput={(params): JSX.Element => (
            <HHTextField
              {...params}
              hhVariant="variant-bypass"
              variant="outlined"
              size="small"
              InputLabelProps={{ sx: { color: 'gray' } }}
              data-testid="verifier-inquiry-date"
            />
          )}
        />
        <HHLoadingButton
          hhVariant="contained"
          color="primary"
          loading={loading}
          disabled={submitDisabled}
          onClick={(): void => {
            if (!dateValid || !inquiryDate) return;
            setMockResult(undefined);
            setSearchParams({
              [HH_USER_UUID_PARAM]: hhUserUuid,
              [INQUIRY_DATE_PARAM]: inquiryDate.format(INQUIRY_DATE_FORMAT),
            });
            void run({
              hhUserUuid,
              inquiryDate: inquiryDate.format(INQUIRY_DATE_FORMAT),
              useCase: USE_CASE,
              fromCache,
            });
          }}
          layoutStyles={{ marginTop: '4px' }}
          data-testid="verifier-submit"
        >
          Submit
        </HHLoadingButton>
        <HHFormControlLabel
          control={
            <HHSwitch
              hhVariant="primary"
              checked={showRealValues}
              onChange={(): void => setShowRealValues(v => !v)}
              data-testid="verifier-show-real-values"
            />
          }
          label="Show PHI"
        />
        <HHIconButton
          hhVariant="variant-bypass"
          onClick={(e): void => setSettingsAnchor(e.currentTarget)}
          data-testid="verifier-settings-button"
          title="Settings"
        >
          <SettingsOutlinedIcon />
        </HHIconButton>
        <Popover
          open={settingsAnchor !== null}
          anchorEl={settingsAnchor}
          onClose={(): void => setSettingsAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <HHStack padding={2}>
            <HHFormControlLabel
              control={
                <HHSwitch
                  hhVariant="primary"
                  checked={fromCache}
                  onChange={(): void => setFromCache(v => !v)}
                  data-testid="verifier-from-cache"
                />
              }
              label="Use HingeSelect cache results"
            />
          </HHStack>
        </Popover>
        {MOCK_ENABLED &&
          MOCK_LOADERS.map(({ label, testId, load }) => (
            <HHButton
              key={testId}
              hhVariant="variant-bypass"
              variant="outlined"
              color="secondary"
              onClick={(): void => setMockResult(load())}
              layoutStyles={{ marginTop: '4px' }}
              data-testid={testId}
            >
              {label}
            </HHButton>
          ))}
      </HHStack>

      {result?.memberAccountId && (
        <HHTypography
          hhVariant="body2"
          color="text.secondary"
          data-testid="verifier-member-account-id"
        >
          Member Account ID: {result.memberAccountId}
          {result.internal && (
            <>
              {' • '}
              From cache: {result.internal.fromCache === true ? 'Yes' : 'No'}
            </>
          )}
        </HHTypography>
      )}
      {result?.memberAccountError && (
        <HHAlert hhVariant="standard" severity="error">
          Failed to resolve member account id:{' '}
          {result.memberAccountError.message}
        </HHAlert>
      )}

      {mockResult && (
        <HHAlert
          hhVariant="standard"
          severity="error"
          onClose={(): void => setMockResult(undefined)}
        >
          Showing <strong>synthetic mock data</strong> — no eligibility check
          was performed. These values mean nothing about a real member.
        </HHAlert>
      )}

      {result && (
        <>
          {comparison.kind === 'compared' && (
            <HHStack spacing={3}>
              <HHStack direction="row" spacing={2} alignItems="center">
                <HHTypography hhVariant="h6">
                  {comparison.provider} comparison
                </HHTypography>
                <HHChip
                  hhVariant="variant-bypass"
                  size="small"
                  color={divergent === 0 ? 'success' : 'error'}
                  label={
                    divergent === 0
                      ? 'No divergence'
                      : `${divergent} diverging field${
                          divergent === 1 ? '' : 's'
                        }`
                  }
                  data-testid="verifier-divergence-chip"
                />
              </HHStack>
              <DiffTable rows={comparison.rows} blurred={blurred} />

              {comparison.extraColumns.length > 0 && (
                <>
                  <HHStack direction="row" spacing={2} alignItems="center">
                    <HHTypography hhVariant="h6">
                      Efile Extra Columns
                    </HHTypography>
                    <HHChip
                      hhVariant="variant-bypass"
                      size="small"
                      color={extraDivergent === 0 ? 'success' : 'error'}
                      label={
                        extraDivergent === 0
                          ? 'No divergence'
                          : `${extraDivergent} diverging field${
                              extraDivergent === 1 ? '' : 's'
                            }`
                      }
                      data-testid="verifier-extra-columns-chip"
                    />
                  </HHStack>
                  <DiffTable rows={comparison.extraColumns} blurred={blurred} />
                </>
              )}

              {/* Context rows are provider-specific and some models have none
                  — e-file has no upstream call outcome to frame — so the whole
                  section goes away rather than rendering an empty table. */}
              {comparison.context.length > 0 && (
                <>
                  <HHTypography hhVariant="h6">
                    Context (not compared)
                  </HHTypography>
                  <DiffTable rows={comparison.context} blurred={blurred} />
                </>
              )}

              <HHTypography hhVariant="h6">
                Projected {comparison.provider} member info
              </HHTypography>
              <HHTypography hhVariant="body2" color="text.secondary">
                The final object each path produces — what the{' '}
                {comparison.provider} writer would consume. Fields shown as{' '}
                <code>undefined</code> are absent from that side&apos;s
                response.
              </HHTypography>
              <HHStack direction="row" spacing={4} alignItems="flex-start">
                <PayloadPanel
                  title="From legacy"
                  titleVariant="subtitle1"
                  payload={comparison.models.legacy}
                  blurred={blurred}
                />
                <PayloadPanel
                  title="From internal"
                  titleVariant="subtitle1"
                  payload={comparison.models.internal}
                  blurred={blurred}
                />
              </HHStack>
            </HHStack>
          )}

          {comparison.kind === 'providerMismatch' && (
            <HHAlert hhVariant="standard" severity="error">
              The two paths resolved to different providers — legacy{' '}
              {comparison.legacyProvider} vs internal{' '}
              {comparison.internalProvider}. Nothing is comparable; inspect the
              raw payloads below.
            </HHAlert>
          )}

          {comparison.kind === 'noModel' && (
            <HHAlert hhVariant="standard" severity="info">
              No comparison model covers these responses — legacy reported{' '}
              {comparison.legacyProvider ?? 'no provider'}, internal reported{' '}
              {comparison.internalProvider ?? 'no provider'}. Raw payloads are
              below.
            </HHAlert>
          )}

          {comparison.kind === 'incomplete' && (
            <HHAlert hhVariant="standard" severity="warning">
              Only one side returned, so there is nothing to compare.
            </HHAlert>
          )}

          <HHStack direction="row" spacing={4} alignItems="flex-start">
            <PayloadPanel
              title="Legacy eligibility service (raw)"
              payload={result.legacy}
              error={result.legacyError}
              blurred={blurred}
              collapsible
            />
            <PayloadPanel
              title="HingeSelect internal eligibility (raw)"
              payload={result.internal}
              error={result.internalError}
              blurred={blurred}
              collapsible
            />
          </HHStack>
        </>
      )}
    </HHStack>
  );
};

export default EligibilityVerifier;
